import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/api';

const AuthContext = createContext(null);

// Check session every 30 seconds
const SESSION_CHECK_INTERVAL = 30000;

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem('mm_token'));
  const [user, setUser] = useState(() => {
    const s = localStorage.getItem('mm_user');
    return s ? JSON.parse(s) : null;
  });

  // Force logout - clears state and redirects
  const forceLogout = useCallback((reason = 'Session expired') => {
    localStorage.removeItem('mm_token');
    localStorage.removeItem('mm_user');
    setToken(null);
    setUser(null);
    // Show message and redirect
    if (window.location.pathname !== '/login') {
      sessionStorage.setItem('logout_reason', reason);
      window.location.href = '/login';
    }
  }, []);

  // Periodic session validation
  useEffect(() => {
    if (!token) return;

    const checkSession = async () => {
      try {
        // Light endpoint to validate session
        await api.get('/auth/me');
      } catch (err) {
        if (err.response?.status === 401) {
          forceLogout('You were logged out from another device');
        }
      }
    };

    // Check immediately on mount
    checkSession();

    // Then check periodically
    const interval = setInterval(checkSession, SESSION_CHECK_INTERVAL);
    
    // Also check when tab becomes visible (user returns to tab)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [token, forceLogout]);

  const login = async (email, password, forceLogoutOther = false) => {
    try {
      const res = await api.post('/auth/login', { email, password }, {
        params: forceLogoutOther ? { force_logout: true } : {}
      });
      const { access_token, user: u } = res.data;
      localStorage.setItem('mm_token', access_token);
      localStorage.setItem('mm_user', JSON.stringify(u));
      setToken(access_token);
      setUser(u);
      return { success: true, user: u };
    } catch (err) {
      // Check for session limit exceeded
      if (err.response?.status === 409 && err.response?.data?.detail?.code === 'SESSION_LIMIT_EXCEEDED') {
        return {
          success: false,
          sessionLimitExceeded: true,
          message: err.response.data.detail.message,
          currentSessions: err.response.data.detail.current_sessions,
          maxSessions: err.response.data.detail.max_sessions,
        };
      }
      throw err;
    }
  };

  const logout = async () => {
    // Clear local state FIRST so the UI reacts instantly — otherwise /login bounces
    // back to the dashboard (token still present) and every request 401s.
    const previousToken = localStorage.getItem('mm_token');
    localStorage.removeItem('mm_token');
    localStorage.removeItem('mm_user');
    setToken(null);
    setUser(null);
    if (previousToken) {
      try {
        await api.post('/auth/logout', null, { headers: { Authorization: `Bearer ${previousToken}` } });
      } catch (e) {
        // Session already gone server-side — nothing to do
      }
    }
  };

  return (
    <AuthContext.Provider value={{ token, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
